import { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  CheckCircle2, XCircle, AlertCircle, ArrowLeft, ArrowRight, GraduationCap, BookOpen, FileText, Clock, BarChart2, ExternalLink, Check, X, Target, Tag as TagIcon, HelpCircle, Award, Flame, MoreHorizontal, Trash2, ChevronDown
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import { StructuredSolutionRenderer, StructuredSolutionData } from '@/components/shared/StructuredSolutionRenderer'
import { QuestionContextCard, QuestionContextData } from '@/components/shared/QuestionContextCard'
import { ZoomableImage } from '@/components/shared/FullscreenMediaModal'
import { useToast } from '@/hooks/use-toast'
import { resolveBackNavigation } from '@/lib/utils'

interface MappedEntity { id: number; name?: string; title?: string }
interface TagItem { id: number; name: string }

interface Translation {
  language: string
  question_text: string
  hint: string | null
  explanation_text: string | null
  status: string
  quality_score: number | null
  minor_edit_pending: boolean
  question_image_url?: string | null
  explanation_image_url?: string | null
  options: Array<{
    id: number
    index: number
    label: string
    is_correct: boolean
    text: string | null
    option_image_url?: string | null
  }>
}

interface QuestionStats {
  times_attempted: number
  times_correct: number
  times_incorrect: number
  accuracy_rate: number
  avg_time_spent: number
}

interface QuestionDetail {
  id: number
  external_id: string
  question_type: string
  render_format: string
  difficulty: string
  target_time_seconds: number | null
  correct_option_index: number | null
  numerical_answer: number | null
  is_pyq: boolean
  is_exemplar: boolean
  status: string
  quality_score: number | null
  version: number
  figure_image_url?: string | null
  structured_solution?: StructuredSolutionData | null
  primary_subject: string | null
  primary_chapter: string | null
  primary_section?: MappedEntity | null
  primary_standard?: MappedEntity | null
  question_context?: QuestionContextData | null
  tags?: TagItem[]
  mapped_exams?: MappedEntity[]
  mapped_syllabi?: MappedEntity[]
  mapped_tests?: MappedEntity[]
  mapped_standards?: MappedEntity[]
  stats: QuestionStats
  pyq_info?: {
    exam_name?: string
    year?: number | string
    shift?: string
    frequency?: number
  } | null
}

interface Review {
  id: number
  language: string | null
  verdict: string
  notes: string | null
  quality_score: number | null
  is_minor_edit: boolean
  reviewed_at: string
  reviewer: string | null
}

interface Props {
  question: QuestionDetail
  next_draft_question_id?: number | null
  next_question_id?: number | null
  prev_question_id?: number | null
  translations: Translation[]
  reviews: Review[]
}

function ReviewPanel({
  questionId, language, currentStatus, isConceptPanel
}: {
  questionId: number; language: string | null; currentStatus: string; isConceptPanel: boolean
}) {
  const { toast } = useToast()
  const [notes, setNotes] = useState('')
  const [qualityScore, setQualityScore] = useState<string>('')
  const [isMinorEdit, setIsMinorEdit] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (verdict: string) => {
    setSubmitting(true)
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const body = {
      question_review: {
        language: language,
        verdict: verdict,
        notes,
        quality_score: qualityScore ? Number(qualityScore) : null,
        is_minor_edit: isMinorEdit,
        criteria: {},
      }
    }
    try {
      const res = await fetch(`/admin/questions/${questionId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        toast({
          title: "Review saved",
          description: `Verdict "${verdict}" recorded successfully.`,
        })
        router.reload()
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to record review.",
        })
      }
    } catch (e) {
      console.error(e)
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred.",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-2xl border border-border/80 bg-card p-3.5 sm:p-4 shadow-xs space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-border/60">
        <div className="flex items-center gap-1.5 min-w-0">
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <span className="font-bold text-xs sm:text-sm text-foreground truncate">
            {isConceptPanel ? 'Concept Review' : `${language?.toUpperCase()} Translation Review`}
          </span>
        </div>
        <Badge
          variant="outline"
          className={`capitalize text-[10px] sm:text-xs font-semibold shrink-0 ${
            currentStatus === 'published'
              ? 'bg-secondary/15 text-secondary border-secondary/30'
              : currentStatus === 'approved'
              ? 'bg-primary/15 text-primary border-primary/30'
              : currentStatus === 'needs_revision' || currentStatus === 'rejected'
              ? 'bg-destructive/15 text-destructive border-destructive/30'
              : 'bg-muted/70 text-foreground border-border'
          }`}
        >
          {currentStatus.replace('_', ' ')}
        </Badge>
      </div>

      {/* Reviewer Notes */}
      <div>
        <Textarea
          placeholder="Review notes, rationale, or guidance..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="text-xs bg-background/80 border-border/80 rounded-xl resize-none min-h-[56px] focus-visible:ring-primary/20"
          rows={2}
        />
      </div>

      {/* Quality Score & Minor Edit Controls */}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center justify-between px-2.5 py-1.5 rounded-xl bg-muted/40 border border-border/60">
          <span className="text-[11px] text-muted-foreground font-medium">Score</span>
          <input
            type="number"
            min="0"
            max="100"
            className="h-6 w-12 rounded-md border border-border/80 bg-background text-center text-xs font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="0-100"
            value={qualityScore}
            onChange={(e) => setQualityScore(e.target.value)}
          />
        </div>

        {!isConceptPanel ? (
          <div className="flex items-center justify-between px-2.5 py-1.5 rounded-xl bg-muted/40 border border-border/60">
            <Label htmlFor={`minor-edit-${language}`} className="text-[11px] text-muted-foreground font-medium cursor-pointer select-none">
              Minor edit
            </Label>
            <Switch
              id={`minor-edit-${language}`}
              checked={isMinorEdit}
              onCheckedChange={setIsMinorEdit}
              className="scale-85 origin-right"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center px-2.5 py-1.5 rounded-xl bg-muted/20 border border-border/40 text-[10px] text-muted-foreground italic">
            Concept Audit
          </div>
        )}
      </div>

      {/* Verdict Action Buttons Grid */}
      <div className="grid grid-cols-3 gap-1.5 pt-0.5">
        <Button
          size="sm"
          className="h-8.5 text-xs font-bold bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-2xs rounded-xl px-1"
          disabled={submitting}
          onClick={() => handleSubmit('approved')}
        >
          <CheckCircle2 className="mr-1 h-3.5 w-3.5 shrink-0" />
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8.5 text-xs font-bold text-primary border-primary/40 hover:bg-primary/10 rounded-xl px-1"
          disabled={submitting}
          onClick={() => handleSubmit('needs_revision')}
        >
          <AlertCircle className="mr-1 h-3.5 w-3.5 shrink-0" />
          Revision
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8.5 text-xs font-bold text-destructive border-destructive/40 hover:bg-destructive/10 rounded-xl px-1"
          disabled={submitting}
          onClick={() => handleSubmit('rejected')}
        >
          <XCircle className="mr-1 h-3.5 w-3.5 shrink-0" />
          Reject
        </Button>
      </div>
    </div>
  )
}

export default function QuestionsShow({
  question,
  next_draft_question_id,
  next_question_id,
  prev_question_id: _prev_question_id,
  translations,
  reviews,
}: Props) {
  const [activeTab, setActiveTab] = useState(translations[0]?.language || 'concept')
  const isPyq = Boolean(question.is_pyq || question.pyq_info?.year || question.pyq_info?.exam_name)
  const { toast } = useToast()
  const [quickSubmitting, setQuickSubmitting] = useState(false)

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/questions', 'Questions')

  const returnUrlForLinks = `/admin/questions/${question.id}`

  const handleQuickReview = async (verdict: string) => {
    setQuickSubmitting(true)
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const currentLang = activeTab === 'concept' ? null : activeTab
    const body = {
      question_review: {
        language: currentLang,
        verdict: verdict,
        notes: '',
        quality_score: null,
        is_minor_edit: false,
        criteria: {},
      }
    }
    try {
      const res = await fetch(`/admin/questions/${question.id}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        toast({
          title: "Review saved",
          description: `Verdict "${verdict.replace('_', ' ')}" recorded successfully.`,
        })
        router.reload()
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to record review.",
        })
      }
    } catch (e) {
      console.error(e)
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred.",
      })
    } finally {
      setQuickSubmitting(false)
    }
  }

  return (
    <AppShell>
      <div className="space-y-4 lg:space-y-6">
        {/* Mobile Top Header (lg:hidden) */}
        <div className="lg:hidden space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" asChild className="h-8 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground font-medium">
              <Link href={backHref}>
                <ArrowLeft className="h-4 w-4" />
                <span>Questions</span>
              </Link>
            </Button>

            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className={`text-[10px] capitalize font-bold ${
                  question.status === 'published'
                    ? 'bg-secondary/15 text-secondary border-secondary/30'
                    : question.status === 'approved'
                    ? 'bg-primary/15 text-primary border-primary/30'
                    : question.status === 'needs_revision' || question.status === 'rejected'
                    ? 'bg-destructive/15 text-destructive border-destructive/30'
                    : 'bg-muted text-foreground border-border'
                }`}
              >
                {question.status.replace('_', ' ')}
              </Badge>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                    title="More actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 bg-card border-border shadow-md">
                  <DropdownMenuItem
                    onClick={() => handleQuickReview('rejected')}
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2 text-xs"
                  >
                    <XCircle className="h-3.5 w-3.5 text-destructive" />
                    <span>Reject Question</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem
                    onClick={() => {
                      if (confirm('Are you sure you want to permanently delete this question?')) {
                        router.delete(`/admin/questions/${question.id}`)
                      }
                    }}
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2 text-xs font-medium"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    <span>Delete Question</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile Quick Action Strip (Horizontally Scrollable) */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 gap-1 text-[11px] font-semibold text-secondary border-secondary/40 hover:bg-secondary/10 shrink-0 rounded-full"
              disabled={quickSubmitting}
              onClick={() => handleQuickReview('approved')}
            >
              <CheckCircle2 className="h-3 w-3" />
              <span>Approve</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 gap-1 text-[11px] font-semibold text-primary border-primary/40 hover:bg-primary/10 shrink-0 rounded-full"
              disabled={quickSubmitting}
              onClick={() => handleQuickReview('needs_revision')}
            >
              <AlertCircle className="h-3 w-3" />
              <span>Needs Revision</span>
            </Button>

            {question.status !== 'published' && (
              <Button
                size="sm"
                variant="default"
                className="h-7 px-3 gap-1 text-[11px] font-semibold shadow-xs shrink-0 rounded-full bg-primary text-primary-foreground"
                onClick={() => router.post(`/admin/questions/${question.id}/publish`)}
              >
                <Check className="h-3 w-3" />
                <span>Publish</span>
              </Button>
            )}

            {(next_draft_question_id || next_question_id) && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-7 px-3 gap-1 text-[11px] font-semibold bg-card hover:bg-muted text-foreground border-border shadow-2xs shrink-0 rounded-full"
              >
                <Link href={`/admin/questions/${next_draft_question_id || next_question_id}`}>
                  <span>Next Draft</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Top Header (hidden lg:flex) */}
        <div className="hidden lg:flex flex-wrap items-center justify-between gap-4">
          <Button variant="ghost" size="sm" asChild className="gap-2">
            <Link href={backHref}>
              <ArrowLeft className="h-4 w-4" /> {backLabel}
            </Link>
          </Button>

          <div className="flex flex-wrap items-center gap-2">
            {/* Quick Review Decisions */}
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2.5 gap-1.5 text-xs font-semibold text-secondary border-secondary/40 hover:bg-secondary/10"
              disabled={quickSubmitting}
              onClick={() => handleQuickReview('approved')}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Approve</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2.5 gap-1.5 text-xs font-semibold text-primary border-primary/40 hover:bg-primary/10"
              disabled={quickSubmitting}
              onClick={() => handleQuickReview('needs_revision')}
            >
              <AlertCircle className="h-3.5 w-3.5" />
              <span>Needs Revision</span>
            </Button>

            <div className="h-4 w-px bg-border mx-1 hidden sm:block" />

            {/* Primary Workflow Actions (Handy: Publish & Next Draft Question) */}
            {question.status !== 'published' ? (
              <Button
                size="sm"
                variant="default"
                className="h-8 px-3 gap-1.5 text-xs font-semibold shadow-xs"
                onClick={() => router.post(`/admin/questions/${question.id}/publish`)}
              >
                <Check className="h-3.5 w-3.5" />
                <span>Publish</span>
              </Button>
            ) : (
              <Badge
                variant="outline"
                className="h-8 px-2.5 gap-1.5 text-xs font-semibold bg-secondary/15 text-secondary border-secondary/30"
              >
                <Check className="h-3.5 w-3.5" />
                <span>Published</span>
              </Badge>
            )}

            {(next_draft_question_id || next_question_id) && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-8 px-3 gap-1.5 text-xs font-semibold bg-card hover:bg-muted text-foreground border-border shadow-2xs"
              >
                <Link href={`/admin/questions/${next_draft_question_id || next_question_id}`}>
                  <span>Next Draft Question</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}

            <div className="h-4 w-px bg-border mx-1 hidden sm:block" />

            {/* Destructive Actions (Safely isolated to prevent accidental clicks) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                  title="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 bg-card border-border shadow-md">
                <DropdownMenuItem
                  onClick={() => handleQuickReview('rejected')}
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2 text-xs"
                >
                  <XCircle className="h-3.5 w-3.5 text-destructive" />
                  <span>Reject Question</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem
                  onClick={() => {
                    if (confirm('Are you sure you want to permanently delete this question?')) {
                      router.delete(`/admin/questions/${question.id}`)
                    }
                  }}
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2 text-xs font-medium"
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  <span>Delete Question</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          {/* Left 2 Columns: Question Preview & Translation Tabs */}
          <div className="lg:col-span-2 space-y-4">
            {question.question_context && (
              <QuestionContextCard
                context={question.question_context}
                format={question.render_format}
              />
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between pb-1">
                <TabsList className="bg-muted/70 p-1 border border-border rounded-xl inline-flex gap-1 h-auto">
                  {translations.map((t) => (
                    <TabsTrigger
                      key={t.language}
                      value={t.language}
                      className="px-3 py-1 text-xs font-semibold rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                    >
                      {t.language.toUpperCase()}
                    </TabsTrigger>
                  ))}
                  <TabsTrigger
                    value="concept"
                    className="px-3 py-1 text-xs font-semibold rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                  >
                    Concept
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="concept" className="space-y-4 pt-2">
                <Card className="shadow-xs border-border/80 bg-card rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 sm:px-5 bg-muted/40 border-b border-border/60 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-primary" />
                      <span className="font-bold text-sm text-foreground">Concept & Taxonomy Metadata</span>
                    </div>
                    {question.target_time_seconds && (
                      <Badge variant="outline" className="text-xs font-semibold gap-1 bg-card border-border/80">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {question.target_time_seconds}s
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4 sm:p-5 space-y-3 text-xs">
                    <div className="divide-y divide-border/60 rounded-xl border border-border/60 bg-muted/20 overflow-hidden">
                      <div className="flex items-center justify-between p-2.5 sm:p-3 gap-2">
                        <span className="text-muted-foreground font-medium text-[11px] sm:text-xs shrink-0">Subject</span>
                        <span className="font-bold text-foreground text-right text-xs sm:text-sm">{question.primary_subject ?? '—'}</span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 sm:p-3 gap-2">
                        <span className="text-muted-foreground font-medium text-[11px] sm:text-xs shrink-0">Chapter</span>
                        <span className="font-bold text-foreground text-right text-xs sm:text-sm">{question.primary_chapter ?? '—'}</span>
                      </div>
                      {question.primary_section?.name && (
                        <div className="flex items-center justify-between p-2.5 sm:p-3 gap-2">
                          <span className="text-muted-foreground font-medium text-[11px] sm:text-xs shrink-0">Section</span>
                          <span className="font-semibold text-foreground text-right text-xs">{question.primary_section.name}</span>
                        </div>
                      )}
                      {question.primary_standard?.name && (
                        <div className="flex items-center justify-between p-2.5 sm:p-3 gap-2">
                          <span className="text-muted-foreground font-medium text-[11px] sm:text-xs shrink-0">Standard</span>
                          <span className="font-semibold text-primary text-right text-xs">{question.primary_standard.name}</span>
                        </div>
                      )}
                    </div>

                    {(question.is_pyq || question.pyq_info?.year || question.pyq_info?.exam_name) && (
                      <div className="p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/25 space-y-2">
                        <div className="flex items-center gap-1.5 text-tertiary font-bold text-xs">
                          <GraduationCap className="h-4 w-4 shrink-0" />
                          <span>Historical PYQ Intelligence</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs pt-0.5">
                          <div className="p-2 rounded-lg bg-card/80 border border-border/50">
                            <span className="text-muted-foreground block text-[10px]">Target Exam</span>
                            <strong className="text-foreground font-bold text-xs truncate block">{question.pyq_info?.exam_name || 'Standard Exam'}</strong>
                          </div>
                          <div className="p-2 rounded-lg bg-card/80 border border-border/50">
                            <span className="text-muted-foreground block text-[10px]">Year / Shift</span>
                            <strong className="text-foreground font-bold text-xs block">{question.pyq_info?.year || '—'} {question.pyq_info?.shift ? `(${question.pyq_info.shift})` : ''}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <ReviewPanel
                  questionId={question.id}
                  language={null}
                  currentStatus={question.status}
                  isConceptPanel={true}
                />
              </TabsContent>

              {translations.map((t) => (
                <TabsContent key={t.language} value={t.language} className="space-y-4 pt-2">
                  {/* Attempt Screen Inspired Question Card */}
                  <Card className="shadow-xs border-border bg-card rounded-2xl overflow-hidden">
                    {/* Attempt Header Strip: Subject/Chapter, Difficulty, Type, Marks/Time in a single line */}
                    <div className="px-3.5 py-2.5 sm:px-5 bg-muted/40 border-b border-border flex items-center gap-1.5 text-xs overflow-x-auto no-scrollbar whitespace-nowrap">
                      {question.primary_subject && (
                        <Badge variant="outline" className="text-[10px] sm:text-[11px] px-2 py-0.5 bg-card border-border font-medium text-foreground shrink-0 max-w-[160px] sm:max-w-xs truncate" title={question.primary_subject + (question.primary_chapter ? ` › ${question.primary_chapter}` : '')}>
                          <BookOpen className="mr-1 h-3 w-3 text-primary shrink-0 inline" />
                          <span className="truncate">{question.primary_chapter || question.primary_subject}</span>
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] sm:text-[11px] px-1.5 py-0.5 uppercase bg-muted text-foreground border-border font-medium shrink-0">
                        {question.question_type.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-[11px] px-1.5 py-0.5 font-semibold shrink-0">
                        {question.difficulty}
                      </Badge>
                      {question.target_time_seconds && (
                        <span className="text-[10px] sm:text-[11px] text-muted-foreground font-mono flex items-center gap-1 px-1.5 py-0.5 rounded bg-card border border-border shrink-0">
                          <Clock className="h-2.5 w-2.5 text-muted-foreground" />
                          {question.target_time_seconds}s
                        </span>
                      )}
                    </div>

                    <CardContent className="p-4 sm:p-6 space-y-5">
                      {/* Question Body with Attempt-Screen Typography */}
                      <div className="text-base sm:text-lg text-foreground font-serif leading-relaxed">
                        <KatexRenderer
                          content={t.question_text || ''}
                          format={question.render_format}
                          highlightOptions={t.options.map(o => ({ text: o.text || undefined }))}
                        />
                      </div>

                      {/* Question Figure Image (Language-neutral) */}
                      {question.figure_image_url && (
                        <ZoomableImage
                          src={question.figure_image_url}
                          alt="Question figure"
                          title="Question Figure"
                          caption="Question Figure"
                          allowDownload={true}
                          downloadFilename={`question-${question.id}-figure`}
                        />
                      )}

                      {/* Question Translation Image */}
                      {t.question_image_url && (
                        <ZoomableImage
                          src={t.question_image_url}
                          alt={`Question image (${t.language})`}
                          title={`Question Image (${t.language.toUpperCase()})`}
                          allowDownload={true}
                          downloadFilename={`question-${question.id}-${t.language}-image`}
                        />
                      )}

                      {/* Options List (Attempt-Screen Style) */}
                      <div className="space-y-2.5 pt-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                          Answer Options
                        </span>
                        {t.options.map((opt) => (
                          <div
                            key={opt.id}
                            className={`p-3 sm:p-4 rounded-xl border flex items-start gap-2.5 sm:gap-3 transition-all ${
                              opt.is_correct
                                ? 'bg-secondary/10 border-secondary/40 text-foreground ring-1 ring-secondary/30'
                                : 'bg-card/70 border-border hover:bg-muted/30'
                            }`}
                          >
                            <span className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                              opt.is_correct
                                ? 'bg-secondary text-secondary-foreground shadow-2xs'
                                : 'bg-muted text-foreground/80 border border-border/80'
                            }`}>
                              {opt.label}
                            </span>
                            <div className="flex-1 min-w-0 text-sm sm:text-base space-y-2 leading-relaxed">
                              {opt.text && <KatexRenderer content={opt.text} format={question.render_format} />}
                              {opt.option_image_url && (
                                <ZoomableImage
                                  src={opt.option_image_url}
                                  alt={`Option ${opt.label}`}
                                  title={`Option ${opt.label} Image`}
                                  className="max-h-40 w-auto max-w-full object-contain rounded-lg border border-border/50 bg-background/50 p-1"
                                  containerClassName="my-1 p-1 rounded-lg border border-border/60 bg-muted/10 flex flex-col items-center"
                                  allowDownload={true}
                                  downloadFilename={`question-${question.id}-option-${opt.label}`}
                                />
                              )}
                              {opt.is_correct && (
                                <div className="pt-0.5">
                                  <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary text-[10px] sm:text-[11px] h-5 sm:h-6 gap-1 px-2 font-bold inline-flex">
                                    <CheckCircle2 className="h-3 w-3" />
                                    <span>Correct Answer</span>
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Hint Banner (if available) */}
                      {t.hint && (
                        <div className="p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/25 text-xs space-y-1">
                          <span className="font-bold text-tertiary flex items-center gap-1.5">
                            <HelpCircle className="h-3.5 w-3.5" /> Hint
                          </span>
                          <div className="text-foreground leading-relaxed pt-0.5">
                            <KatexRenderer content={t.hint} format={question.render_format} />
                          </div>
                        </div>
                      )}

                      {/* Structured Solution / Explanation */}
                      {(question.structured_solution || t.explanation_text || t.explanation_image_url) && (
                        <div className="pt-3 border-t border-border/60 space-y-3">
                          <StructuredSolutionRenderer
                            solution={question.structured_solution}
                            fallbackExplanation={t.explanation_text}
                            format={question.render_format}
                            allowDownload={true}
                          />
                          {t.explanation_image_url && (
                            <ZoomableImage
                              src={t.explanation_image_url}
                              alt="Explanation diagram"
                              title="Solution Diagram"
                              caption="Solution Diagram"
                              allowDownload={true}
                              downloadFilename={`question-${question.id}-solution-diagram`}
                            />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <ReviewPanel
                    questionId={question.id}
                    language={t.language}
                    currentStatus={t.status}
                    isConceptPanel={false}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Right Column: PYQ Benchmark, Taxonomy Context & Performance Analytics Sidebar */}
          <div className="space-y-6">
            {/* Card 0: Historical PYQ & High-Yield Intelligence */}
            {(isPyq || question.is_exemplar) && (
              <Card
                className={
                  isPyq
                    ? 'border-tertiary/35 bg-tertiary/5 shadow-glass-xs rounded-2xl overflow-hidden'
                    : 'border-secondary/35 bg-secondary/5 shadow-glass-xs rounded-2xl overflow-hidden'
                }
              >
                <CardHeader
                  className={
                    isPyq
                      ? 'pb-3 bg-tertiary/10 border-b border-tertiary/20'
                      : 'pb-3 bg-secondary/10 border-b border-secondary/20'
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle
                      className={`text-sm font-bold flex items-center gap-2 ${
                        isPyq ? 'text-tertiary' : 'text-secondary'
                      }`}
                    >
                      {isPyq ? (
                        <GraduationCap className="h-4 w-4 text-tertiary shrink-0" />
                      ) : (
                        <Award className="h-4 w-4 text-secondary shrink-0" />
                      )}
                      <span>{isPyq ? 'Historical PYQ Intelligence' : 'High-Yield Standard'}</span>
                    </CardTitle>
                    {isPyq && (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-bold bg-tertiary/20 text-tertiary border-tertiary/30"
                      >
                        Active PYQ
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3 text-xs">
                  {isPyq && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2.5 rounded-xl bg-card/80 border border-border/60">
                        <span className="text-[10px] text-muted-foreground font-semibold block">Target Exam</span>
                        <span className="font-extrabold text-foreground text-xs mt-0.5 block truncate">
                          {question.pyq_info?.exam_name || 'Standard Exam'}
                        </span>
                      </div>
                      <div className="p-2.5 rounded-xl bg-card/80 border border-border/60">
                        <span className="text-[10px] text-muted-foreground font-semibold block">Year / Shift</span>
                        <span className="font-extrabold text-foreground text-xs mt-0.5 block">
                          {question.pyq_info?.year || '2025'}{' '}
                          {question.pyq_info?.shift ? `(${question.pyq_info.shift})` : ''}
                        </span>
                      </div>
                    </div>
                  )}

                  {Boolean(question.pyq_info?.frequency && question.pyq_info.frequency > 1) && (
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-primary/10 border border-primary/25 text-primary font-bold text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <Flame className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span>Repeat Frequency:</span>
                      </div>
                      <span>{question.pyq_info?.frequency}x in Past Exams</span>
                    </div>
                  )}

                  {question.is_exemplar && (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-secondary/10 border border-secondary/25 text-secondary font-bold text-[11px]">
                      <Award className="h-4 w-4 text-secondary shrink-0" />
                      <span>Official Exemplar High-Yield Standard</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Card 1: Question Context & Mapped Entities */}
            <Card className="shadow-xs border-border/80 bg-card/60 backdrop-blur-xs rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2 text-foreground">
                  <BookOpen className="h-4 w-4 text-primary" />
                  Taxonomy & Mapping Context
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 space-y-4 text-xs">
                {/* Subject & Chapter */}
                <div className="grid grid-cols-2 gap-3 pb-3 border-b border-border/50">
                  <div>
                    <span className="text-muted-foreground block text-[11px] font-medium">Subject</span>
                    <span className="font-bold text-sm text-foreground">{question.primary_subject || '—'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px] font-medium">Chapter</span>
                    <span className="font-bold text-sm text-foreground">{question.primary_chapter || '—'}</span>
                  </div>
                  {question.primary_section && (
                    <div>
                      <span className="text-muted-foreground block text-[11px] font-medium">Section</span>
                      <span className="font-medium text-xs text-foreground">{question.primary_section.name}</span>
                    </div>
                  )}
                  {question.primary_standard && (
                    <div>
                      <span className="text-muted-foreground block text-[11px] font-medium">Standard</span>
                      <Link
                        href={`/admin/standards/${question.primary_standard.id}?return_to=${encodeURIComponent(returnUrlForLinks)}`}
                        className="font-medium text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        {question.primary_standard.name}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  )}
                </div>

                {/* Linked Exams */}
                <div className="space-y-1.5">
                  <span className="text-muted-foreground text-[11px] font-semibold flex items-center gap-1">
                    <GraduationCap className="h-3.5 w-3.5 text-primary" /> Mapped Exams ({question.mapped_exams?.length || 0})
                  </span>
                  {question.mapped_exams && question.mapped_exams.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {question.mapped_exams.map((ex) => (
                        <Link
                          key={ex.id}
                          href={`/admin/exams/${ex.id}?return_to=${encodeURIComponent(returnUrlForLinks)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary hover:bg-primary/15 text-[11px] font-medium transition-colors"
                        >
                          {ex.name}
                          <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-[11px] block italic">No exams mapped</span>
                  )}
                </div>

                {/* Linked Syllabi */}
                <div className="space-y-1.5">
                  <span className="text-muted-foreground text-[11px] font-semibold flex items-center gap-1">
                    <BookOpen className="h-3.5 w-3.5 text-secondary" /> Mapped Syllabi ({question.mapped_syllabi?.length || 0})
                  </span>
                  {question.mapped_syllabi && question.mapped_syllabi.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {question.mapped_syllabi.map((sy) => (
                        <Link
                          key={sy.id}
                          href={`/admin/syllabi/${sy.id}?return_to=${encodeURIComponent(returnUrlForLinks)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary hover:bg-secondary/15 text-[11px] font-medium transition-colors"
                        >
                          {sy.name}
                          <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-[11px] block italic">No syllabi mapped</span>
                  )}
                </div>

                {/* Linked Tests */}
                <div className="space-y-1.5 pb-2 border-b border-border/50">
                  <span className="text-muted-foreground text-[11px] font-semibold flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5 text-tertiary" /> Mapped Tests ({question.mapped_tests?.length || 0})
                  </span>
                  {question.mapped_tests && question.mapped_tests.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {question.mapped_tests.map((t) => (
                        <Link
                          key={t.id}
                          href={`/admin/tests/${t.id}?return_to=${encodeURIComponent(returnUrlForLinks)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-tertiary/10 border border-tertiary/20 text-tertiary hover:bg-tertiary/15 text-[11px] font-medium transition-colors"
                        >
                          {t.title}
                          <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-[11px] block italic">No tests mapped</span>
                  )}
                </div>

                {/* Tags & Taxonomy Labels */}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-muted-foreground text-[11px] font-semibold flex items-center gap-1">
                    <TagIcon className="h-3.5 w-3.5 text-primary" /> Question Tags
                  </span>
                  {question.tags && question.tags.length > 0 ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border/70 bg-muted/40 hover:bg-muted text-foreground text-xs font-semibold transition-colors"
                        >
                          <TagIcon className="h-3 w-3 text-primary" />
                          <span>{question.tags.length} Tags</span>
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-3 text-xs bg-card border-border shadow-md rounded-xl" align="end">
                        <div className="font-semibold text-foreground text-xs pb-2 border-b border-border/60 mb-2 flex items-center justify-between">
                          <span>Attached Tags</span>
                          <span className="text-muted-foreground text-[11px] font-normal">{question.tags.length} total</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                          {question.tags.map((t) => (
                            <Link
                              key={t.id}
                              href={`/admin/questions?tags[]=${encodeURIComponent(t.name)}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-border/70 bg-background hover:bg-primary/5 hover:border-primary/40 hover:text-primary text-[11px] font-mono text-muted-foreground transition-colors"
                            >
                              #{t.name}
                            </Link>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span className="text-muted-foreground text-[11px] block italic">No tags</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Student Performance Analytics */}
            <Card className="shadow-xs border-border/80 bg-card/60 backdrop-blur-xs rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2 text-foreground">
                  <BarChart2 className="h-4 w-4 text-primary" />
                  Student Performance Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 space-y-4 text-xs">
                {/* Accuracy Progress Gauge */}
                <div className="space-y-1.5 p-3 rounded-xl bg-muted/40 border border-border/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-muted-foreground">Accuracy Rate</span>
                    <span className="font-bold text-sm text-primary">{question.stats.accuracy_rate}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-primary h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.max(0, question.stats.accuracy_rate))}%` }}
                    />
                  </div>
                </div>

                {/* Metrics Breakdown Grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-card border border-border/70 shadow-2xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Target className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-[11px] text-muted-foreground font-medium truncate">Attempts</span>
                    </div>
                    <span className="text-xs font-bold text-foreground shrink-0 pl-1">{question.stats.times_attempted}</span>
                  </div>

                  <div className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-card border border-border/70 shadow-2xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Clock className="h-3.5 w-3.5 text-tertiary shrink-0" />
                      <span className="text-[11px] text-muted-foreground font-medium truncate">Avg Time</span>
                    </div>
                    <span className="text-xs font-bold text-foreground shrink-0 pl-1">
                      {question.stats.avg_time_spent ? `${question.stats.avg_time_spent}s` : '—'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-secondary/10 border border-secondary/20">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <CheckCircle2 className="h-3.5 w-3.5 text-secondary shrink-0" />
                      <span className="text-[11px] text-secondary font-semibold truncate">Correct</span>
                    </div>
                    <span className="text-xs font-bold text-secondary shrink-0 pl-1">
                      {question.stats.times_correct}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-2 sm:p-2.5 rounded-xl bg-destructive/10 border border-destructive/20">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <X className="h-3.5 w-3.5 text-destructive shrink-0" />
                      <span className="text-[11px] text-destructive font-semibold truncate">Incorrect</span>
                    </div>
                    <span className="text-xs font-bold text-destructive shrink-0 pl-1">
                      {question.stats.times_incorrect}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Review History */}
            <Card className="shadow-xs border-border/80 bg-card/60 backdrop-blur-xs rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2 text-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Review History ({reviews.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-5 space-y-3 text-xs">
                {reviews.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No reviews recorded yet.</p>
                ) : (
                  reviews.map((r) => (
                    <div key={r.id} className="border-b border-border/50 last:border-0 pb-3 last:pb-0 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-foreground">{r.reviewer || 'Reviewer'}</span>
                        <span className="text-muted-foreground text-[11px]">{r.reviewed_at}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium">
                          {r.language ? r.language.toUpperCase() : 'Concept'}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`text-[10px] capitalize font-bold ${
                            r.verdict === 'approved'
                              ? 'bg-secondary/15 text-secondary border-secondary/30'
                              : r.verdict === 'rejected'
                              ? 'bg-destructive/15 text-destructive border-destructive/30'
                              : 'bg-primary/15 text-primary border-primary/30'
                          }`}
                        >
                          {r.verdict.replace('_', ' ')}
                        </Badge>
                        {r.quality_score && (
                          <span className="text-[11px] text-muted-foreground font-medium">
                            Score: <strong className="text-foreground">{r.quality_score}</strong>/100
                          </span>
                        )}
                      </div>
                      {r.notes && (
                        <p className="text-xs text-muted-foreground mt-1 bg-muted/40 p-2 rounded-lg border border-border/40">
                          {r.notes}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
